<template>
  <RAFTFullScreenContent header-text="Incident Reports">
    <div v-if="getUpdatingReportsStatus === 'success'" id="table-ready" />
    <div>
      <a
        id="csv-export-link"
        data-test="csv-export-link"
        href="#"
        @click="incidentReportsPostToCsvDownload"
        ><b-img
          :src="getCsvIconPath"
          width="24"
          height="24"
          fluid
          alt="CSV Download"
      /></a>
    </div>
    <VFilterGroup
      :show-submit="false"
      :show-clear="false"
      :show-advanced-option="false"
      class="incident-report-listing-filters"
    >
      <template #filters>
        <single-select-filter
          id="report-type"
          label="Report Type"
          :update-method="updateReportType"
          path="field_incident_report.field_in_report_type_of_report"
          operator="="
          :multiple="false"
          :get-value="reportTypes"
          :selected-items="[reportType]"
          :disable-empty-selection="false"
        />
        <single-select-filter
          v-if="allDepartments && allDepartments.length > 0"
          id="department-filter"
          label="Department/Unit"
          :update-method="updateDepartment"
          path="field_inc_invest_department_unit"
          operator="="
          :multiple="false"
          :get-value="allDepartments"
          display-value="title"
          track-by="nid"
          :disable-empty-selection="false"
        />
        <single-select-filter
          v-if="
            incidentManagementTeamMembers &&
            incidentManagementTeamMembers.length > 0
          "
          id="assigned-to-filter"
          label="Assigned To"
          :update-method="updateAssignedTo"
          path="field_inc_invst_team_assigned"
          operator="="
          :multiple="false"
          :get-value="incidentManagementTeamMembers"
          :disable-empty-selection="false"
        />
        <multi-select-filter
          id="report-status-filter"
          label="Status"
          :update-method="updateReportStatus"
          path="field_inc_invest_report_status_value"
          :get-value="investigationStatusOptions"
          :disable-empty-selection="false"
          :current-set-value="reportStatus"
          track-by="key"
          display-value="label"
        />
        <date-filter
          :date-between="true"
          :get-value="reportedAfterDate"
          :update-method="updateDateBetweenStart"
          :end-date-get-value="reportedBeforeDate"
          :end-date-update-method="updateDateBetweenEnd"
          label="Reported After"
          end-label="Reported Before"
          id="date-filter"
        />
        <single-select-filter
          v-if="allCampuses && allCampuses.length > 0"
          :get-value="allCampuses"
          id="campus-fil"
          :multiple="false"
          label="Site/Campus"
          display-value="title"
          track-by="nid"
          :disable-empty-selection="false"
          :update-method="updateSelectedCampus"
        />
        <single-select-filter
          :get-value="filteredBuildings"
          id="building-fil"
          :multiple="false"
          label="Building"
          display-value="title"
          track-by="nid"
          :disable-empty-selection="false"
          :update-method="updateSelectedBuilding"
        />
        <single-select-filter
          :get-value="buildingSpaces"
          id="space-fil"
          :multiple="false"
          label="Space/Room"
          display-value="title"
          track-by="nid"
          :disable="spaceFilterDisabled"
          :disable-empty-selection="false"
          :update-method="updateSpace"
          :selected-items="selectedSpace"
        />
        <single-select-filter
          :get-value="getGeneralCategoryOptions"
          id="gencat-fil"
          :multiple="false"
          label="General Category"
          :disable-empty-selection="false"
          :update-method="updateGeneralCategory"
          track-by="value"
          display-value="text"
        />
        <single-select-filter
          :get-value="getReportableCategoryOptions"
          id="repcat-fil"
          :multiple="false"
          label="Reportable Category"
          :disable-empty-selection="false"
          :update-method="updateReportableCategory"
          track-by="value"
          display-value="text"
        />
        <single-select-filter
          :get-value="getAreaTypeOptions"
          id="areatype-fil"
          :multiple="false"
          label="Area Type"
          :disable-empty-selection="false"
          :update-method="updateAreaType"
          track-by="value"
          display-value="text"
        />
        <multi-select-filter
          id="bls-code-filter"
          label="BLS Codes"
          :update-method="updateBlsCodes"
          path="field_inc_invest_report_status_value"
          :get-value="blsCodeList"
          :disable-empty-selection="false"
          :current-set-value="selectedBlsCodes"
          track-by="key"
          display-value="label"
        />
      </template>
    </VFilterGroup>

    <BaseTable
      :items="items"
      :fields="fields"
      :total-items="totalItems"
      :items-per-page="itemsPerPage"
      :current-page="currentPage"
      :is-busy="getUpdatingReportsStatus === 'busy'"
      class-prefix="incident-reports"
      @update-current-page="updateCurrentPage"
      ref="table"
      :no-local-sorting="true"
      @sort-changed="sortingChanged"
    >
      <template #empty>
        <div class="empty-text">
          There are currently no incident reports to list.
        </div>
      </template>
      <template #link-cell="data">
        <a v-if="data?.data?.value?.url" :href="data.data.value.url">{{
          data.data.value.text
        }}</a>
        <span v-else>{{ data.data.value.text }}</span>
      </template>
    </BaseTable>
    <notifications position="bottom right" group="alerts" />
    <notifications
      position="bottom right"
      group="downloads"
      :duration="downloadProgressNotificationDuration"
    />
  </RAFTFullScreenContent>
</template>

<script>
import BaseTable from "../raft/BaseTable.vue";
import DateFilter from "../filters/DateFilter.vue";
import JsonApiFilterManager from "../../lib/JsonApiFilterManager";
import PathMapper from "../../lib/PathMapper";
import RAFTFullScreenContent from "../RAFTFullScreenContent.vue";
import raftGenericHelper from "../../lib/raft_generic_helper.js";
import SingleSelectFilter from "../filters/SingleSelectFilter.vue";
import MultiSelectFilter from "../filters/MultiSelectFilter.vue";
import { restClient, jsonClient } from "../../lib/raftApi";
import VFilterGroup from "../raft/VFilterGroup.vue";

export default {
  name: "IncidentReportsListing",
  components: {
    BaseTable,
    DateFilter,
    RAFTFullScreenContent,
    SingleSelectFilter,
    MultiSelectFilter,
    VFilterGroup,
  },
  data() {
    return {
      allCampuses: [],
      allBuildings: [],
      allDepartments: [],
      buildingSpaces: [],
      blsCodes: [],
      items: [],
      investigations: null,
      reportedAfterDate: null,
      reportedBeforeDate: null,
      reportedAfter: null,
      reportedBefore: null,
      reportType: null,
      department: null,
      assignedTo: null,
      selectedCampus: null,
      selectedBuilding: null,
      selectedSpace: null,
      generalCategory: null,
      reportableCategory: null,
      areaType: null,
      reportStatus: [],
      selectedBlsCodes: [],
      getUpdatingReportsStatus: null,
      downloadProgressNotificationDuration: 18000,
      totalItems: 0,
      itemsPerPage: 25,
      currentPage: 1,
      sortDesc: false,
      spaceFilterDisabled: true,
      sortBy: null,
      reportTypes: {
        incident_report: "Incident Report",
        near_miss: "Near Miss",
      },
      investigationStatuses: {
        submitted: "Submitted",
        collecting_details: "Collecting Details",
        in_progress: "Investigation in Progress",
        closed: "Closed",
      },
      investigationStatusOptions: [
        { key: "submitted", label: "Submitted" },
        { key: "collecting_details", label: "Collecting Details" },
        { key: "in_progress", label: "Investigation in Progress" },
        { key: "closed", label: "Closed" },
      ],
      incidentManagementTeamMembers: [],
      baseQueries: {
        include:
          "field_incident_report,field_incident_report.field_in_report_campus,field_incident_report.field_in_report_building,field_incident_report.field_in_report_space_room,field_incident_report.uid,field_inc_invest_group,field_inc_invest_department_unit,field_inc_invest_person_involved,field_inc_invst_team_assigned",
        "fields[node--department]": "title,drupal_internal__nid",
        "fields[node--campus]": "title,drupal_internal__nid",
        "fields[node--building]": "title,drupal_internal__nid",
        "fields[node--space]": "title,drupal_internal__nid",
        "fields[node--laboratory]": "title,drupal_internal__nid",
        "fields[user--user]": "field_prof_display_name,drupal_internal__uid",
        sort: "-field_inc_invest_report_status,-created",
      },
      blsCodeKeys: [
        "blsCodes",
        "event_1",
        "event_2",
        "event_3",
        "event_4",
        "nature_1",
        "nature_2",
        "nature_3",
        "nature_4",
        "bodypart_1",
        "bodypart_2",
        "bodypart_3",
        "bodypart_4",
        "source_1",
        "source_2",
        "source_3",
        "source_4",
      ],
      investigationFields: null,
    };
  },
  async created() {
    this.handleUrlFilterParams();
    await this.getIncidentReports();
    await this.getIncidentManagementTeamMembers();
    let response;
    try {
      response = await restClient.get({ path: "/campuses", queries: {} });
      this.allCampuses = response.campuses;
      response = await restClient.get({ path: "/buildings", queries: {} });
      this.allBuildings = response.buildings;
      response = await restClient.get({ path: "/departments", queries: {} });
      this.allDepartments = response.departments;
      response = await restClient.get({
        path: "/incident_management/bls_oiics_codes",
        queries: {},
      });
      if (response.results.length > 0) {
        this.blsCodes = response.results;
      }
    } catch (error) {
      this.$showNotification(
        "error",
        `Error retrieving data: ${error.message}`,
      );
    }
    await this.fetchInvestigationFields();
  },
  methods: {
    async fetchInvestigationFields() {
      const options = {
        path: "/entity-view-display/node/incident_investigation",
        queries: {
          display_id: "default",
        },
      };
      const response = await restClient
        .get(options)
        .catch((e) => this.errorEmit(e));
      this.investigationFields = response?.incident_investigation.default;
    },
    handleUrlFilterParams() {
      this.reportType = this.$route.query.reportTypeFilter;
      const statusFilter = this.$route.query.statusFilter;
      if (statusFilter && statusFilter.length > 0) {
        statusFilter.forEach((status) => {
          const statusItem = {};
          statusItem.key = status;
          statusItem.label = this.investigationStatuses[status];
          this.reportStatus.push(statusItem);
        });
      }
    },
    async getIncidentReports(resetPager = true) {
      this.getUpdatingReportsStatus = "busy";
      this.setDefaultFilters();
      this.setUpdatedFilters();
      if (!resetPager && this.currentPage > 1) {
        JsonApiFilterManager.setPageOffset(
          (this.currentPage - 1) * this.itemsPerPage,
        );
      } else {
        JsonApiFilterManager.setPageOffset(0);
        this.currentPage = 1;
      }
      if (this.sortBy) {
        JsonApiFilterManager.setSortFields(this.sortBy);
      }
      const options = {
        path: "node/incident_investigation",
        queries: JsonApiFilterManager.queries,
      };
      const response = await jsonClient.get(options).catch((error) => {
        this.$showNotification(
          "error",
          `Unable to load data. ${error.message}.`,
        );
      });
      if (response) {
        this.investigations = response?.deserialized;
        this.totalItems = parseInt(response?.meta?.count, 10);
        this.items = [];
        this.investigations.forEach((investigation) => {
          const item = {
            form_id: {
              url:
                "/raft/incident-management/incident-report/" +
                investigation.field_incident_report.id +
                "/view",
              text: investigation.field_incident_report
                .field_incident_report_number,
              custom_slot: "link-cell",
            },
            campus: investigation.field_incident_report.field_in_report_campus
              ? investigation.field_incident_report.field_in_report_campus.title
              : "",
            building: investigation.field_incident_report
              .field_in_report_building
              ? investigation.field_incident_report.field_in_report_building
                  .title
              : "",
            space_room: investigation.field_incident_report
              .field_in_report_space_room
              ? investigation.field_incident_report.field_in_report_space_room
                  .title
              : "",
            report_type:
              this.reportTypes[
                investigation.field_incident_report
                  .field_in_report_type_of_report
              ],
            submitted_by: this.getSubmittedBy(investigation),
            date_submitted: raftGenericHelper.formatDateFilterYyyyMmDd(
              new Date(investigation.field_incident_report.created),
            ),
            person_involved: this.getPersonInvolved(investigation),
            group: this.getGroup(investigation),
            department: this.getDepartment(investigation),
            assigned_to: this.getAssignedTo(investigation),
            status:
              this.investigationStatuses[
                investigation.field_inc_invest_report_status
              ],
            general_category: this.getGeneralCategory(investigation),
            reportable_category: this.getReportableCategory(investigation),
            area_type: this.getAreaType(investigation),
            investigate: null,
          };
          // Currently everyone that can access this page can also access these links, but check access for
          // future use cases.
          item.investigate = {
            custom_slot: "link-cell",
          };
          if (investigation.user_edit_access) {
            item.investigate.url =
              "/raft/incident-management/incident-investigation/" +
              investigation.id +
              "/edit";
            item.investigate.text = "Investigate";
          }
          this.items.push(item);
        });
        this.getUpdatingReportsStatus = "success";
      }
    },
    setDefaultFilters() {
      JsonApiFilterManager.queries = this.baseQueries;
      JsonApiFilterManager.queries["page[limit]"] = this.itemsPerPage;
      JsonApiFilterManager.addFilter("status", "status", 1);
      JsonApiFilterManager.addFilter("operative_status", "operative_status", 1);
    },
    setUpdatedFilters() {
      const filters = {
        start_date: {
          path: "field_incident_report.created",
          value: this.reportedAfter,
          operator: ">",
        },
        end_date: {
          path: "field_incident_report.created",
          value: this.reportedBefore,
          operator: "<",
        },
        report_type: {
          path: "field_incident_report.field_in_report_type_of_report",
          value: this.reportType,
          operator: "=",
        },
        department: {
          path: "field_inc_invest_department_unit.nid",
          value: this.department,
          operator: "=",
        },
        assigned_to: {
          path: "field_inc_invst_team_assigned.uid",
          value: this.assignedTo,
          operator: "=",
        },
        report_status: {
          path: "field_inc_invest_report_status",
          value: this.reportStatus,
          operator: "=",
        },
        bls_codes: {
          path: "field_bls_codes",
          value: this.selectedBlsCodes,
          operator: "IN",
        },
        campus: {
          path: "field_incident_report.field_in_report_campus.nid",
          value: this.selectedCampus,
          operator: "=",
        },
        building: {
          path: "field_incident_report.field_in_report_building.nid",
          value: this.selectedBuilding,
          operator: "=",
        },
        space: {
          path: "field_incident_report.field_in_report_space_room.nid",
          value: this.selectedSpace,
          operator: "=",
        },
        general_category: {
          path: "field_inc_invest_gen_category",
          value: this.generalCategory,
          operator: "=",
        },
        reportable_category: {
          path: "field_inc_invest_rep_category",
          value: this.reportableCategory,
          operator: "=",
        },
        area_type: {
          path: "field_inc_invest_area_type",
          value: this.areaType,
          operator: "=",
        },
      };
      Object.entries(filters).forEach(([name, filter]) => {
        if (name === "report_status") {
          if (filter.value && filter.value.length > 0) {
            const values = filter.value.map((item) => item.key);
            JsonApiFilterManager.addMultivalueNamedFilter(
              name,
              filter.path,
              values,
            );
          } else {
            JsonApiFilterManager.deleteFiltersByName([name]);
          }
        } else if (name === "bls_codes") {
          JsonApiFilterManager.deleteFiltersByName(this.blsCodeKeys);
          if (filter.value && filter.value.length > 0) {
            const codeTypes = {};
            filter.value.forEach((code) => {
              if (!codeTypes.hasOwnProperty(code.code_type)) {
                codeTypes[code.code_type] = [];
              }
              codeTypes[code.code_type].push(code.key);
            });
            Object.keys(codeTypes).forEach((key) => {
              JsonApiFilterManager.addMultivalueNamedFilterWithGroupOr(
                key,
                filter.path + "." + key,
                codeTypes[key],
                "",
                "",
                "blsCodes",
              );
            });
          }
        } else if (filter.value) {
          JsonApiFilterManager.addFilter(
            name,
            filter.path,
            filter.value,
            filter.operator,
          );
        } else {
          JsonApiFilterManager.deleteFiltersByName([name]);
        }
      });
    },
    getSubmittedBy(investigation) {
      let submittedName = "Anonymous";
      let submittedUrl = null;
      if (
        !investigation.field_incident_report.field_in_report_anon_submission
      ) {
        if (
          investigation.field_incident_report.uid.drupal_internal__uid === 1
        ) {
          submittedName = "Admin";
        } else {
          submittedName =
            investigation.field_incident_report.uid.field_prof_display_name;
          submittedUrl =
            "/user/" +
            investigation.field_incident_report.uid.drupal_internal__uid;
        }
      }
      return {
        url: submittedUrl,
        text: submittedName,
        custom_slot: "link-cell",
      };
    },
    getGroup(investigation) {
      if (!investigation.field_inc_invest_group) {
        return "";
      }
      return {
        url:
          "/node/" + investigation.field_inc_invest_group.drupal_internal__nid,
        text: investigation.field_inc_invest_group.title,
        custom_slot: "link-cell",
      };
    },
    getDepartment(investigation) {
      if (
        !investigation.field_inc_invest_department_unit ||
        investigation.field_inc_invest_department_unit.length < 1
      ) {
        return "";
      }
      return {
        url:
          "/node/" +
          investigation.field_inc_invest_department_unit[0]
            .drupal_internal__nid,
        text: investigation.field_inc_invest_department_unit[0].title,
        custom_slot: "link-cell",
      };
    },
    getPersonInvolved(investigation) {
      if (!investigation.field_inc_invest_person_involved) {
        return "";
      }
      return {
        url:
          "/user/" +
          investigation.field_inc_invest_person_involved.drupal_internal__uid,
        text: investigation.field_inc_invest_person_involved
          .field_prof_display_name,
        custom_slot: "link-cell",
      };
    },
    getAssignedTo(investigation) {
      if (!investigation.field_inc_invst_team_assigned) {
        return "";
      }
      return {
        url:
          "/user/" +
          investigation.field_inc_invst_team_assigned.drupal_internal__uid,
        text: investigation.field_inc_invst_team_assigned
          .field_prof_display_name,
        custom_slot: "link-cell",
      };
    },
    getGeneralCategory(investigation) {
      const category = investigation.field_inc_invest_gen_category;
      if (!category) {
        return "";
      }

      const allowedValues =
        this.investigationFields?.field_inc_invest_gen_category?.definition
          ?.settings?.allowed_values;
      const values = this.processAllowedValues(allowedValues);

      if (category in values) {
        return values[category];
      }

      return "";
    },
    getReportableCategory(investigation) {
      if (!investigation.field_inc_invest_rep_category) {
        return "";
      }
      const cats = [];
      const values = this.processAllowedValues(
        this.investigationFields?.field_inc_invest_rep_category?.definition
          ?.settings?.allowed_values,
      );
      investigation.field_inc_invest_rep_category.forEach((item) => {
        cats.push(values[item]);
      });
      return cats.join(", ");
    },
    getAreaType(investigation) {
      if (!investigation.field_inc_invest_area_type) {
        return "";
      }
      const values = this.processAllowedValues(
        this.investigationFields?.field_inc_invest_area_type?.definition
          ?.settings?.allowed_values,
      );
      return values[investigation.field_inc_invest_area_type] || "";
    },
    processAllowedValues(orgAllowedVals) {
      let allowedVals = {};
      // Check to see if we have an array to convert to a simple mapping for use later.
      if (Array.isArray(orgAllowedVals)) {
        orgAllowedVals.forEach((opt) => {
          allowedVals[opt.key] = opt.title;
        });
      } else {
        allowedVals = orgAllowedVals || {};
      }

      return allowedVals;
    },
    updateCurrentPage(page) {
      this.currentPage = page;
      // Avoid an extra fetach when the page is first built.
      if (this.investigations !== null) {
        this.getIncidentReports(false);
      }
    },
    updateDateBetweenStart(value) {
      this.reportedAfterDate = value || null;
      this.reportedAfter = value
        ? raftGenericHelper.datepickerValueToUnixTimestamp(value)
        : null;
      this.getIncidentReports();
    },
    updateDateBetweenEnd(value) {
      this.reportedBeforeDate = value || null;
      this.reportedBefore = value
        ? raftGenericHelper.datepickerValueToUnixTimestamp(value)
        : null;
      this.getIncidentReports();
    },
    updateReportType(value) {
      this.reportType = value || null;
      this.getIncidentReports();
    },
    updateDepartment(value) {
      this.department = value || null;
      this.getIncidentReports();
    },
    updateAssignedTo(value) {
      this.assignedTo = value || null;
      this.getIncidentReports();
    },
    updateReportStatus(value) {
      this.reportStatus = value || [];
      this.getIncidentReports();
    },
    updateBlsCodes(value) {
      this.selectedBlsCodes = value || [];
      this.getIncidentReports();
    },
    updateSelectedCampus(value) {
      this.selectedCampus = value || null;
      if (value) {
        this.updateSelectedBuilding(null);
      }
      this.getIncidentReports();
    },
    async updateSelectedBuilding(value) {
      this.spaceFilterDisabled = true;
      this.selectedSpace = null;
      this.buildingSpaces = [];
      this.selectedBuilding = value || null;
      if (value) {
        const response = await restClient.get({
          path: "/spaces",
          queries: { parent_location: [value] },
        });
        this.buildingSpaces = response.spaces;
        this.spaceFilterDisabled = false;
      }
      await this.getIncidentReports();
    },
    updateSpace(value) {
      this.selectedSpace = value || null;
      this.getIncidentReports();
    },
    updateGeneralCategory(value) {
      this.generalCategory = value || null;
      this.getIncidentReports();
    },
    updateReportableCategory(value) {
      this.reportableCategory = value || null;
      this.getIncidentReports();
    },
    updateAreaType(value) {
      this.areaType = value || null;
      this.getIncidentReports();
    },
    sortingChanged(ctx) {
      this.sortDesc = !ctx.sortDesc;
      const sortDir = this.sortDesc ? "-" : "";
      const field = this.fields.find(
        (fieldData) => fieldData.key === ctx.sortBy && fieldData.sortable,
      );
      if (field) {
        this.sortBy = sortDir + field.sortPath;
      }
      this.getIncidentReports();
    },
    async getIncidentManagementTeamMembers() {
      this.incidentManagementTeamMembers = [];
      const options = {
        path: "/incident_management/team-members",
        queries: {},
      };
      const data = await restClient.get(options).catch(() => {
        this.$showNotification(
          "error",
          "Unable to load Incident Management team member list.",
        );
      });
      if (data) {
        Object.values(data).forEach((item) => {
          const option = {
            value: item.uid,
            title: item.display_name,
          };
          this.incidentManagementTeamMembers.push(option);
        });
      }
    },
    incidentReportsPostToCsvDownload() {
      const filters = {};
      if (this.reportedAfter) {
        filters["created[min]"] = raftGenericHelper.formatDateFilterYyyyMmDd(
          this.reportedAfterDate,
        );
      }
      if (this.reportedBefore) {
        filters["created[max]"] = raftGenericHelper.formatDateFilterYyyyMmDd(
          this.reportedBeforeDate,
        );
      }
      if (this.reportType) {
        filters.field_in_report_type_of_report_value = this.reportType;
      }
      if (this.department) {
        filters.field_inc_invest_department_unit_target_id = this.department;
      }
      if (this.assignedTo) {
        filters.field_inc_invst_team_assigned_target_id = this.assignedTo;
      }
      if (this.selectedCampus) {
        filters.field_in_report_campus_target_id = this.selectedCampus;
      }
      if (this.selectedBuilding) {
        filters.field_in_report_building_target_id = this.selectedBuilding;
      }
      if (this.selectedSpace) {
        filters.field_in_report_space_room_target_id = this.selectedSpace;
      }
      if (this.generalCategory) {
        filters.field_inc_invest_gen_category = this.generalCategory;
      }
      if (this.reportableCategory) {
        filters.field_inc_invest_rep_category = this.reportableCategory;
      }
      if (this.areaType) {
        filters.field_inc_invest_area_type = this.areaType;
      }
      if (this.reportStatus.length > 0) {
        this.reportStatus.forEach((element, index) => {
          const key = "field_inc_invest_report_status_value[" + index + "]";
          filters[key] = element.key;
        });
      }
      if (this.selectedBlsCodes.length > 0) {
        this.selectedBlsCodes.forEach((code) => {
          const filterId = code.code_type + "_raft_numeric";
          if (filters.hasOwnProperty(filterId)) {
            filters[filterId] += "," + code.key;
          } else {
            filters[filterId] = code.key;
          }
        });
      }
      restClient.downloadFileStream({
        path: "/raft/rest/incident-management/incident-reports.csv",
        filename: "incident-reports.csv",
        queries: filters,
      });
      this.$showNotification("default", "Generating CSV Export...");
    },
    errorEmit({ message, data }) {
      this.$emit("form-error-message", { message: message, data: data });
    },
  },
  computed: {
    fields: function () {
      let fields = [
        {
          key: "form_id",
          label: "Form ID",
          sortable: true,
          sortPath: "field_incident_report.field_incident_report_number",
        },
        {
          key: "campus",
          label: "Site/Campus",
          sortable: true,
          sortPath: "field_incident_report.field_in_report_campus.title",
        },
        {
          key: "building",
          label: "Building",
          sortable: true,
          sortPath: "field_incident_report.field_in_report_building.title",
        },
        { key: "space_room", label: "Space/Room" },
        { key: "report_type", label: "Type" },
        { key: "submitted_by", label: "Submitted By" },
        {
          key: "date_submitted",
          label: "Date Submitted",
          sortable: true,
          sortPath: "field_incident_report.created",
        },
        {
          key: "person_involved",
          label: "Person Involved",
          sortable: true,
          sortPath: "field_inc_invest_person_involved.field_prof_display_name",
        },
        {
          key: "group",
          label: "Group",
          sortable: true,
          sortPath: "field_inc_invest_group.title",
        },
        {
          key: "department",
          label: "Department/Unit(s)",
          sortable: true,
          sortPath: "field_inc_invest_department_unit.title",
        },
        {
          key: "assigned_to",
          label: "Assigned To",
          sortable: true,
          sortPath: "field_inc_invst_team_assigned.field_prof_display_name",
        },
        {
          key: "status",
          label: "Status",
          sortable: true,
          sortPath: "field_inc_invest_report_status",
        },
        {
          key: "general_category",
          label: "General Category",
          sortable: true,
          sortPath: "field_inc_invest_gen_category",
        },
        {
          key: "reportable_category",
          label: "Reportable Category",
          sortable: true,
          sortPath: "field_inc_invest_rep_category",
        },
        {
          key: "area_type",
          label: "Area Type",
          sortable: true,
          sortPath: "field_inc_invest_area_type",
        },
        {
          key: "investigate",
          label: "Investigate",
          sortable: false,
        },
      ];
      // Remove campus field if we don't have any campuses.
      if (this.allCampuses?.length < 1) {
        fields = fields.filter((field) => field.key !== "campus");
      }
      return fields;
    },
    getGeneralCategoryOptions: function () {
      return this.investigationFields?.field_inc_invest_gen_category?.definition
        ?.settings?.allowed_values;
    },
    getReportableCategoryOptions: function () {
      return this.investigationFields?.field_inc_invest_rep_category?.definition
        ?.settings?.allowed_values;
    },
    getAreaTypeOptions: function () {
      return this.investigationFields?.field_inc_invest_area_type?.definition
        ?.settings?.allowed_values;
    },
    getCsvIconPath: function () {
      return PathMapper.getIconAssetPath("CSV.svg", this.$isB4);
    },
    blsCodeList: function () {
      const codes = this.blsCodes;
      const blsCodes = [];
      if (codes && codes.length > 0) {
        codes.forEach((code) => {
          blsCodes.push({
            key: code.id,
            label: code.title,
            code_type: code.code_type.toLowerCase() + "_" + code.level,
          });
        });
      }
      return blsCodes;
    },
    filteredBuildings() {
      let buildings = this.allBuildings || [];
      const campus = this.selectedCampus;
      if (campus) {
        buildings = buildings.filter(
          (element) => element.parent_location_nid === campus,
        );
      }
      return buildings;
    },
  },
};
</script>

<style scoped>
#csv-export-link {
  display: inline-block;
  margin: 0 0 0.5rem 0;
}

.incident-report-listing-filters :deep(select#department-filter),
:deep(select#building-fil),
:deep(select#space-fil) {
  max-width: 190px;
}
.incident-report-listing-filters :deep(select#assigned-to-filter) {
  max-width: 220px;
}
</style>
